// « Le radar suggère » (conception, § 8) : sous la prochaine action de la fiche et dans « Ma journée ». « Prendre »
// en fait la prochaine action (dans 3 jours ouvrés), ou l'ajoute à la note si une action plus proche existe.
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Sparkles } from "lucide-react";
import { type Identifier, useNotify, useTranslate } from "ra-core";
import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";

import { getSupabaseClient } from "../providers/supabase/supabase";
import type { DealSuggestion } from "../types";
import { dueDate, today } from "./nextAction";

export const DealSuggestions = ({
  dealIds,
  canWrite,
}: {
  dealIds: Identifier[];
  canWrite: boolean;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const queryClient = useQueryClient();
  // Suggestion en cours de traitement : un double clic ne renvoie pas la demande.
  const [pending, setPending] = useState<Identifier | null>(null);
  const inFlight = useRef(false);
  const t = (key: string, options?: object) =>
    translate(`resources.deals.suggestions.${key}`, options);
  const { data: suggestions } = useQuery({
    queryKey: ["deal_suggestions", dealIds],
    enabled: dealIds.length > 0,
    queryFn: async () => {
      const { data, error } = await getSupabaseClient()
        .from("deal_suggestions")
        .select("id, deal_id, kind, reason, created_at")
        .in("deal_id", dealIds)
        .is("accepted_at", null)
        .is("dismissed_at", null)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as DealSuggestion[];
    },
  });
  if (!suggestions?.length) return null;

  const run = async (
    id: Identifier,
    call: () => PromiseLike<{ error: { message: string } | null }>,
  ) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setPending(id);
    try {
      const { error } = await call();
      if (error) notify(error.message, { type: "error" });
      queryClient.invalidateQueries({ queryKey: ["deals"] });
      queryClient.invalidateQueries({ queryKey: ["my_day"] });
      await queryClient.invalidateQueries({ queryKey: ["deal_suggestions"] });
    } finally {
      inFlight.current = false;
      setPending(null);
    }
  };
  const reason = (s: DealSuggestion) =>
    s.reason.rule === "phase"
      ? t("reason_phase")
      : s.reason.rule === "company"
        ? t("reason_company", {
            company: s.reason.company,
            role: s.reason.role,
          })
        : t("reason_replied");

  return (
    <ul className="flex flex-col gap-1.5">
      {suggestions.map((s) => {
        const label = translate(`resources.deals.next_action_types.${s.kind}`);
        return (
          <li
            key={s.id}
            className="flex flex-wrap items-center gap-2 rounded-md border border-dashed px-3 py-2 text-sm"
          >
            <Sparkles className="size-4 shrink-0 text-primary" />
            <span className="min-w-0 flex-1">
              {t("title")} : <strong>{label}</strong>
              <span className="text-muted-foreground"> — {reason(s)}</span>
            </span>
            {canWrite && (
              <>
                <Button
                  size="sm"
                  disabled={pending != null}
                  onClick={() =>
                    run(s.id, () =>
                      getSupabaseClient().rpc("accept_suggestion", {
                        suggestion: s.id,
                        due: dueDate("in3", today()),
                        note: label,
                      }),
                    )
                  }
                >
                  {t("take")}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={pending != null}
                  onClick={() =>
                    run(s.id, () =>
                      getSupabaseClient().rpc("dismiss_suggestion", {
                        suggestion: s.id,
                      }),
                    )
                  }
                >
                  {t("dismiss")}
                </Button>
              </>
            )}
          </li>
        );
      })}
    </ul>
  );
};
